import * as React from 'react'
import { Text, View } from 'react-native'
import styled from '@theme/styled-components'

import Picker from '@components/picker/Picker'
import TextInput from '@components/textInput/TextInput'
import Checkbox from '@components/checkbox/Checkbox'
import Animators from '@components/root/Animators'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import * as AuthStore from '@store/auth/auth'
import * as UserStore from '@store/user/user'

import useDebounce from '@utils/useDebounce'

import { FormikHelpers, useFormik } from 'formik'

import { AuthScope } from '@api/enum'

interface Values {
  commentaire: string
  quantite: number
  isPrioritaire: boolean
  edl: boolean
}

const MAX_QUANTITY = 9
const quantityOptions = Array.from(new Array(MAX_QUANTITY)).map((t, idx) => ({ value: idx + 1, label: `${idx + 1}` }))

const Position = ({ form, onForm }: IncidentFormProps) => {
  const init: Values = React.useMemo(
    () => ({
      commentaire: form.commentaire,
      quantite: form.quantite,
      isPrioritaire: form.isPrioritaire,
      edl: form.edl,
    }),
    [form.commentaire, form.quantite, form.isPrioritaire, form.edl]
  )

  const i18n = useI18n()
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)
  const user = useReducer(UserStore.store, (s) => s.user)

  const [current, bounced, setCurrent] = useDebounce(init.commentaire)

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    // set default quantity to 1
    if (!form.quantite) {
      setFieldValue('quantite', 1)
    }
  }, [])

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  React.useEffect(() => {
    // use debounce to limit the number of submit
    setFieldValue('commentaire', bounced)
  }, [bounced])

  const quantity = quantityOptions.find((q) => q.value === values.quantite)

  const displayCheckPiority = scopes.includes(AuthScope.DemandesPrioritaireCreation)
  // TODO : utiliser les scopes EDL
  const displayCheckEDL = !!user && user.typeUtilisateur === 'VIPARIS' && form.isInfrastructure === 'INFRA'

  return (
    <MainContainer>
      <Animators>
        {/* comment */}
        <Title>{i18n.t('screens.incident.DETAIL.comment')}</Title>
        <ZContainer index={2}>
          <TextInput
            value={current}
            onChange={setCurrent}
            placeholder={i18n.t('screens.incident.DETAIL.comment')}
            numberOfLines={6}
          />
        </ZContainer>

        {/* quantity */}
        <Title>{i18n.t('screens.incident.DETAIL.quantity')}</Title>
        <ZContainer index={1}>
          <Picker
            options={quantityOptions}
            value={quantity}
            onChange={(next) => setFieldValue('quantite', next ? next.value : undefined)}
            placeholder={i18n.t('screens.incident.DETAIL.placeholder')}
          />
        </ZContainer>

        {/* urgent */}
        {displayCheckPiority && (
          <CheckContainer>
            <Checkbox
              label={i18n.t('screens.incident.DETAIL.urgent')}
              onClick={() => setFieldValue('isPrioritaire', !values.isPrioritaire)}
              value={values.isPrioritaire}
            />
          </CheckContainer>
        )}

        {/* EDL */}
        {displayCheckEDL && (
          <CheckContainer>
            <Checkbox
              label={i18n.t('screens.incident.DETAIL.edl')}
              onClick={() => setFieldValue('edl', !values.edl)}
              value={values.edl}
              disabled={!!form.edlId}
            />
          </CheckContainer>
        )}
      </Animators>
    </MainContainer>
  )
}

export default Position

const MainContainer = styled(View)`
  margin: 0 20px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin-bottom: 5px;
  margin-top: 15px;
`

const ZContainer = styled(View)<{ index: number }>`
  z-index: ${(props) => props.index};
`

const CheckContainer = styled(View)`
  margin-top: 25px;
`
